import { useState, useEffect, useCallback } from 'react';
import { useToast } from "@/hooks/errors/use-toast";
import { SubscriptionStatus } from '@/types/subscription-types';
// Removed TokenContext dependency - using direct queries instead
import { supabase } from '@/integrations/supabase/client';
import { FREEMIUM_PLAN_ID } from '@/config/constants';

export const useSubscription = () => {
  const [status, setStatus] = useState<SubscriptionStatus | null>({
    hasActiveSubscription: false,
    hasActiveStripeSubscription: false,
    subscription: null,
    customer: null,
    purchasedItems: [],
    tokenUsage: {
      used: 25,
      total: 100,
      remaining: 75
    }
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get current user
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error('User not authenticated');
      }

      // Get token usage from direct database query (simplified)
      let tokenUsage = {
        used: 0,
        total: 0,
        remaining: 0
      };
      
      // Get basic usage data if needed
      try {
        const { data: balanceData } = await supabase
          .from('user_balance_with_usage')
          .select('computed_balance, computed_monthly_usage')
          .eq('user_id', userData.user.id)
          .single();
        
        if (balanceData) {
          tokenUsage = {
            used: balanceData.computed_monthly_usage || 0,
            total: balanceData.computed_balance || 0,
            remaining: Math.max(0, (balanceData.computed_balance || 0) - (balanceData.computed_monthly_usage || 0))
          };
        }
      } catch (balanceError) {
        console.warn('Failed to fetch token usage:', balanceError);
      }

      // Get user's current subscription plan using the database function
      const { data: currentPlan, error: planError } = await supabase
        .rpc('get_user_current_plan', { user_uuid: userData.user.id });

      if (planError) {
        console.warn('Failed to fetch user plan:', planError);
      }

      // Get user profile for Stripe customer ID
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('stripe_customer_id, current_subscription')
        .eq('id', userData.user.id)
        .single();

      if (profileError) {
        console.warn('Failed to fetch user profile:', profileError);
      }

      // Determine if user has a paid subscription (not Freemium)
      const hasActiveSubscription = currentPlan && currentPlan.length > 0 && 
        currentPlan[0]?.plan_name && 
        currentPlan[0].plan_name.toLowerCase() !== 'freemium';

      // If user has a current_subscription that's not Freemium and has a stripe_customer_id,
      // assume they have an active Stripe subscription (safer than querying foreign table)
      const hasActiveStripeSubscription = !!(
        userProfile?.stripe_customer_id && 
        hasActiveSubscription
      );
      
      setStatus({
        hasActiveSubscription: !!hasActiveSubscription,
        hasActiveStripeSubscription,
        subscription: hasActiveSubscription && currentPlan[0] ? {
          id: null, // Will be populated from Stripe subscription data when needed
          status: 'active',
          current_period_end: null, // Will be populated from Stripe data
          plan_name: currentPlan[0].plan_name
        } : null,
        customer: userProfile?.stripe_customer_id ? {
          id: userProfile.stripe_customer_id,
          email: userData.user.email || '',
          name: userData.user.user_metadata?.full_name || userData.user.email || ''
        } : null,
        purchasedItems: [], // Will be populated from transaction history if needed
        tokenUsage
      });
      
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription status');
      // Fallback to free plan status
      setStatus({
        hasActiveSubscription: false,
        hasActiveStripeSubscription: false,
        subscription: null,
        customer: null,
        purchasedItems: [],
        tokenUsage: {
          used: 25,
          total: 100,
          remaining: 75
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array since this function doesn't depend on any props/state

  // Keep the create-checkout function as it successfully sends users to Stripe
  const createCheckoutSession = async (
    priceId: string, 
    mode: 'subscription' | 'payment' = 'subscription',
    customUrls?: { successUrl?: string; cancelUrl?: string }
  ) => {
    try {
      setIsCreatingCheckout(true);
      setError(null);

      if (!priceId) {
        throw new Error('Price ID is required for checkout');
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId,
          mode,
          successUrl: customUrls?.successUrl || `${window.location.origin}/billing?status=success`,
          cancelUrl: customUrls?.cancelUrl || `${window.location.origin}/billing?status=canceled`
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to create checkout session');
      }
      
      // Redirect to Stripe checkout
      if (data?.url) {
        window.location.href = data.url;
        return data;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
      
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout session';
      setError(errorMessage);
      toast({
        title: "Checkout Error",
        description: errorMessage,
        variant: "error",
      });
      throw err; // Re-throw so the button can handle the error state
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  const openCustomerPortal = async (options?: { 
    flowType?: 'subscription_update' | 'subscription_cancel' | 'payment_method_update';
    newPriceId?: string;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      // Check if user has a customer ID
      if (!status?.customer?.id) {
        throw new Error('No Stripe customer found. Please make a purchase first to access subscription management.');
      }

      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { 
          returnUrl: `${window.location.origin}/billing`,
          flowType: options?.flowType,
          newPriceId: options?.newPriceId
        }
      });

      // Handle structured error responses from 404/non-200 status codes
      if (error) {
        console.log('[Subscription] Customer portal error object:', error);
        
        // For FunctionsHttpError, the response body is in error.context.json()
        let errorData = null;
        try {
          // Check if this is a FunctionsHttpError with context
          if (error.context && typeof error.context.json === 'function') {
            errorData = await error.context.json();
          } else if (error.context?.body) {
            errorData = typeof error.context.body === 'string' ? JSON.parse(error.context.body) : error.context.body;
          } else if (error.details) {
            errorData = error.details;
          } else if (error.data) {
            errorData = error.data;
          }
        } catch (e) {
          console.log('[Subscription] Failed to parse error data:', e);
        }
        
        console.log('[Subscription] Parsed error data:', errorData);
        
        // Check if this is our structured NO_ACTIVE_SUBSCRIPTION error
        if (errorData?.error === 'NO_ACTIVE_SUBSCRIPTION' || 
            error.message?.includes('No active subscription found') ||
            errorData?.message?.includes('No active subscription found')) {
          console.log('[Subscription] Detected NO_ACTIVE_SUBSCRIPTION error, returning structured response');
          return {
            success: false,
            error: 'NO_ACTIVE_SUBSCRIPTION',
            message: errorData?.message || 'No active subscription found for subscription update',
            shouldRedirectToCheckout: true,
            requestedPriceId: options?.newPriceId || errorData?.requestedPriceId
          };
        }
        
        // Other errors, throw as usual
        throw new Error(error.message || 'Failed to access customer portal');
      }
      
      // Check for structured error responses in data (shouldn't happen with 404, but keep as fallback)
      if (data?.error === 'NO_ACTIVE_SUBSCRIPTION') {
        // Return the structured error data so caller can handle fallback
        return {
          success: false,
          error: data.error,
          message: data.message,
          shouldRedirectToCheckout: data.shouldRedirectToCheckout,
          requestedPriceId: data.requestedPriceId
        };
      }
      
      // Redirect to Stripe customer portal
      if (data?.url) {
        window.location.href = data.url;
        return { success: true, url: data.url };
      } else {
        throw new Error('No portal URL received from Stripe');
      }
      
    } catch (err) {
      console.error('Customer portal error:', err);
      
      // Check if this is a structured response (not an actual error to throw)
      if (typeof err === 'object' && err !== null && 'success' in err && err.success === false) {
        // This is our structured response, don't throw it
        return err as any;
      }
      
      // This is an actual error, handle it normally
      const errorMessage = err instanceof Error ? err.message : 'Failed to access customer portal';
      setError(errorMessage);
      toast({
        title: "Portal Access Error",
        description: errorMessage,
        variant: "error",
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // New method that handles upgrade attempts with automatic fallback
  const handleUpgradeAttempt = async (priceId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[Subscription] Attempting upgrade with price:', priceId);
      
      // Simple logic: Freemium users go to checkout, paid users go to portal
      if (!status?.hasActiveSubscription) {
        // Freemium users always go to checkout
        console.log('[Subscription] Freemium user - using checkout');
        return await createCheckoutSession(priceId, 'subscription');
      } else {
        // Paid users go to portal for plan changes
        console.log('[Subscription] Paid subscriber - using customer portal');
        const portalResult = await openCustomerPortal({
          flowType: 'subscription_update',
          newPriceId: priceId
        });
        
        // If portal fails for any reason, fall back to checkout
        if (!portalResult.success) {
          console.log('[Subscription] Portal failed, falling back to checkout');
          toast({
            title: "Redirecting to checkout",
            description: "Setting up your new subscription...",
            variant: "default",
          });
          return await createCheckoutSession(priceId, 'subscription');
        }
        
        return portalResult;
      }
    } catch (err) {
      console.error('[Subscription] Upgrade attempt failed:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []); // Only run once on mount

  return {
    status,
    isLoading,
    isCreatingCheckout,
    error,
    refreshStatus: fetchSubscriptionStatus,
    createCheckoutSession,
    openCustomerPortal,
    handleUpgradeAttempt
  };
};
